import React, { Fragment, use, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";

import Loader from "@/shared/components/Loader";
import { LeftBorder } from "@/shared/components/LeftBorder";

import illustration from "@/assets/images/illustration-2.svg";
import illustration_employee from "@/assets/images/illustration-1.svg";

import * as m from "@/shared/styles/main.module.scss";
import useSurveyStore from "../stores/surveyStore";
import { SurveyPlatform } from "../types/common";
import surveyService from "../services/surveyService";
import { PollStatus, SurveyStatus } from "../types/valuebuilder";

export interface StartPageProps {
  preview?: boolean;
  surveyPlatform: SurveyPlatform;
}

const StartPage: React.FC<StartPageProps> = ({ preview, surveyPlatform }) => {
  let { id } = useParams<{ id: string }>();
  const initSurvey = useSurveyStore.use.initSurvey();
  const isSurveyLoading = useSurveyStore.use.isLoading();
  const survey = useSurveyStore.use.survey();
  const isEmployee = useSurveyStore.use.isEmployee();
  const surveyLoadingError = useSurveyStore.use.surveyLoadingError();
  const settings = useSurveyStore.use.settings();

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!id) {
      navigate("/");
      return;
    }
    if (!survey && id) {
      initSurvey(id, surveyPlatform, !!preview);
      localStorage.removeItem("moduleName");
    }
  }, [preview, surveyPlatform]);

  useEffect(() => {
    if (surveyLoadingError) {
      toast.error("Some data could not be loaded please refresh the page");
    }
  }, [surveyLoadingError]);

  const start = async () => {
    if (preview) {
      navigate(`/preview_poll/${id}`);
    } else {
      try {
        setIsLoading(true);

        await surveyService.updatePollStatus(settings!, PollStatus.started);

        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        console.error("Failed to update poll status", error);
        toast.error(
          "Something went wrong, please try again, or refresh a page."
        );
        return;
      }
      localStorage.setItem(
        "moduleName",
        window.location.pathname.substring(
          0,
          window.location.pathname.lastIndexOf("/") + 1
        )
      );
      navigate(`/poll/${id}/`);
    }
  };
  return (
    <>
      <Loader show={isLoading || isSurveyLoading} />
      {!survey ? null : (
        <div className={m.wrapper}>
          <div className={m.start}>
            {survey.status !== undefined &&
            survey.status === SurveyStatus.CLOSED ? (
              <div className={m.borderLabel}>
                <h1>
                  <LeftBorder />
                  {"This survey was closed."}
                </h1>
              </div>
            ) : (
              <Fragment>
                <div className={m.borderLabel}>
                  <h1>
                    <LeftBorder />
                    {survey.title}
                  </h1>
                </div>
                <div className={m.navigateButton}>
                  <button
                    className={`${m.btn} ${m.btnPrimary}`}
                    onClick={start}
                    disabled={surveyLoadingError}
                  >
                    Start Survey
                  </button>
                </div>
                <div className={m.centerImage}>
                  <img
                    src={isEmployee ? illustration_employee : illustration}
                    alt="illustration"
                  />
                </div>
                <div className={m.description}>
                  <pre>{survey.description}</pre>
                </div>
              </Fragment>
            )}
          </div>
        </div>
      )}
    </>
  );
};
export default StartPage;
